/**
 * 頁 21 — 「採購」（職員向供應商落單；P1.5-B；`decisions/015` §2.6／§2.7）
 *
 * ① **安全庫存建議**（低於水平嘅貨品 → 一撳即知要補幾多）
 * ② **落單**（揀供應商 → 揀佢嘅貨（附現時在倉／安全庫存）→ 箱數）
 * ③ **單列表 ＋ 狀態機動作**（`send`／`close`／`cancel`；由後端 `next` 決定，前端唔准自己推）
 *
 * 紅線
 *   · ⭐ 狀態機／建議數量一律由 `@coeldery/purchase-order` 出（後端 route 亦同一支）
 *   · ⭐ 現時在倉 ＝ `/api/purchase-orders/suggestions`（後端同一支「入 − 出」口徑）
 *   · 呢頁只有 `staff`／`admin`（後端 `guard()` 亦 403）
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FormSelectField from '@coeldery/form-select-field';
import FormTextField from '@coeldery/form-text-field';
import { btnGhost, btnPrimary, card, errText, hint as hintStyle, input, label } from '@coeldery/ui-kit';
import PoList from '../components/PoList';
import { ApiError, api, type PurchaseOrder, type PurchaseSuggestion, type Supplier } from '../lib/api';

interface DraftLine {
  itemId: string;
  cartons: string;
}

export default function Purchase() {
  const { t } = useTranslation();
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [items, setItems] = useState<PurchaseSuggestion[]>([]);
  const [shortage, setShortage] = useState<Array<{ itemId: string; itemName: string; suggestCartons: number }>>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [supplierId, setSupplierId] = useState('');
  const [requestedAt, setRequestedAt] = useState('');
  const [note, setNote] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([{ itemId: '', cartons: '' }]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [po, sug, sup] = await Promise.all([
        api.purchaseOrders(),
        api.purchaseSuggestions(),
        api.suppliers(),
      ]);
      setOrders(po.orders);
      setItems(sug.items);
      setShortage(sug.shortage);
      setSuppliers(sup.suppliers ?? []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  /** 只有揀咗供應商先可以揀佢嘅貨（唔准跨供應商落單；後端都會擋） */
  const itemOptions = (sid: string) =>
    items
      .filter((i) => i.supplierId === sid)
      .map((i) => ({
        value: i.itemId,
        label: `${i.itemName}（${i.barcode}）· ${t('po.onHand')} ${i.currentCartons}${
          i.safeLevel === null ? '' : ` · ${t('po.safeLevel')} ${i.safeLevel}`
        }`,
      }));

  const submit = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await api.createPurchaseOrder({
        supplierId,
        requestedAt: requestedAt || null,
        note: note || null,
        lines: lines.filter((l) => l.itemId).map((l) => ({ itemId: l.itemId, cartons: Number(l.cartons) })),
      });
      setNotice(t('po.created', { no: res.poNo }));
      setLines([{ itemId: '', cartons: '' }]);
      setNote('');
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setBusy(false);
    }
  };

  const act = async (po: PurchaseOrder, action: 'send' | 'close' | 'cancel' | 'receive') => {
    if (action === 'cancel' && !window.confirm(t('po.ask.cancel'))) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.purchaseAction(po.id, action);
      setNotice(t(`po.done.${action}`));
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p style={hintStyle}>{t('common.loading')}</p>;

  const nextById = Object.fromEntries(orders.map((o) => [o.id, o.next ?? []]));

  return (
    <>
      <h2 style={{ margin: '0 0 6px', fontSize: '18px', fontWeight: 800 }}>{t('purchase.title')}</h2>
      <p style={hintStyle}>{t('po.intro')}</p>

      {error && (
        <p style={errText} role="alert">
          {error}
        </p>
      )}
      {notice && <p style={{ ...hintStyle, fontWeight: 700, color: 'var(--color-primary)' }}>{notice}</p>}

      {/* ① 安全庫存建議 */}
      <section style={{ ...card, padding: '16px', marginTop: '12px' }}>
        <h3 style={{ margin: '0 0 6px', fontSize: '15px', fontWeight: 800 }}>{t('po.shortageTitle')}</h3>
        <p style={hintStyle}>{t('po.shortageHint')}</p>
        {shortage.length === 0 ? (
          <p style={hintStyle}>{t('po.shortageEmpty')}</p>
        ) : (
          shortage.map((s) => (
            <div
              key={s.itemId}
              style={{ padding: '8px 0', borderTop: '1px solid var(--color-divider)', fontSize: '14px' }}
            >
              <strong>{s.itemName}</strong>
              <span style={hintStyle}> {t('po.suggest', { n: s.suggestCartons })}</span>
            </div>
          ))
        )}
      </section>

      {/* ② 落單 */}
      <section style={{ ...card, padding: '16px', marginTop: '12px' }}>
        <h3 style={{ margin: '0 0 8px', fontSize: '15px', fontWeight: 800 }}>{t('po.newTitle')}</h3>
        <FormSelectField
          id="po-supplier"
          labelKey="po.supplier"
          value={supplierId}
          onChange={(v) => {
            setSupplierId(String(v));
            setLines([{ itemId: '', cartons: '' }]);
          }}
          options={[
            { value: '', label: t('po.pick') },
            ...suppliers.map((s) => ({ value: s.id, label: `${s.code} ${s.name}` })),
          ]}
        />
        <div style={{ marginTop: '10px' }}>
          <label style={label} htmlFor="po-date">
            {t('po.requestedAt')}
          </label>
          <input
            id="po-date"
            type="date"
            style={{ ...input, width: 'auto' }}
            value={requestedAt}
            onChange={(e) => setRequestedAt(e.target.value)}
          />
          <p style={hintStyle}>{t('po.requestedAtHint')}</p>
        </div>

        {lines.map((l, i) => (
          <div key={i} style={{ display: 'grid', gap: '8px', marginTop: '10px' }}>
            <FormSelectField
              id={`po-item-${i}`}
              labelKey="po.item"
              value={l.itemId}
              onChange={(v) => setLines((prev) => prev.map((x, j) => (j === i ? { ...x, itemId: String(v) } : x)))}
              options={[{ value: '', label: t('po.pick') }, ...itemOptions(supplierId)]}
            />
            <FormTextField
              id={`po-cartons-${i}`}
              labelKey="po.cartons"
              value={l.cartons}
              onChange={(v) => setLines((prev) => prev.map((x, j) => (j === i ? { ...x, cartons: String(v) } : x)))}
            />
          </div>
        ))}

        <p style={{ marginTop: '10px', display: 'flex', gap: '8px' }}>
          <button type="button" style={btnGhost} onClick={() => setLines((p) => [...p, { itemId: '', cartons: '' }])}>
            {t('po.addLine')}
          </button>
          {lines.length > 1 && (
            <button type="button" style={btnGhost} onClick={() => setLines((p) => p.slice(0, -1))}>
              {t('po.removeLine')}
            </button>
          )}
        </p>

        <div style={{ marginTop: '10px' }}>
          <FormTextField id="po-note" labelKey="po.note" value={note} onChange={(v) => setNote(String(v))} />
        </div>

        <button
          type="button"
          style={{ ...btnPrimary, marginTop: '12px' }}
          onClick={() => void submit()}
          disabled={busy || !supplierId || !lines.some((l) => l.itemId)}
        >
          {busy ? t('common.saving') : t('po.submit')}
        </button>
      </section>

      {/* ③ 單列表（共用元件；`next` 由後端出） */}
      <PoList
        title={t('po.listTitle')}
        emptyText={t('po.empty')}
        orders={orders}
        nextById={nextById}
        busy={busy}
        onAction={(po, action) => void act(po, action)}
      />
    </>
  );
}

